/**
 * @file Async thunks for getting and patching data for dashtransfer state
 * @author Max van Loosbroek
 */

import { createAsyncThunk } from '@reduxjs/toolkit'
import { IHttpService } from 'angular'
import { RootState } from '../../../common/redux/root.reducer'
import { URLS } from '../../../common/utilities/constants/constants.module'
import { parseHttpError } from '../../../common/utilities/errorHandling'
import {
  DetailedSubscriptionChain,
  DetailedSubscriptionChainResponse,
  DetailedData,
  RobotAccount,
  DetailedRobot,
  DetailedRobotResponse,
  SummarySubscriptionItem,
  SummarySubscription,
  DetailedTaas,
  DetailedTaasResponse
} from '../../overview/types/types'
import { dashtransferBodySelector } from './reducer'
import { OldRelationDetailed, RelationDetailed } from '../../overview/types/RelationDetailed'
import { mapChainDetailedResponseToChainDetailed, mapRobotDetailedResponseToRobotDetailed, mapTaasDetailedResponseToTaasDetailed } from '../../overview/mapResponseType'

export const transferEntities = createAsyncThunk<
any,
void,
{ extra: { $http: IHttpService } }
>('dashtransfer/transferEntities', async (_, thunkApi) => {
  const body = dashtransferBodySelector(thunkApi.getState() as RootState)
  const url = URLS.admin.dashTransfer
  const { data } = await thunkApi.extra.$http
    .post<any>(url, body)
    .catch(parseHttpError)
  return data
})

export const getTransferData = createAsyncThunk<
DetailedData,
{ serialId?: number, chainId?: number, taasId?: number },
{ extra: { $http: IHttpService } }
>(
  'dashtransfer/getTransferData',
  async ({ serialId, chainId, taasId }, thunkApi) => {
    // Get robot
    const robotUrl = `${URLS.admin.robots}/${serialId}`

    const { data: robotResponse } = serialId
      ? await thunkApi.extra.$http
        .get<DetailedRobotResponse>(robotUrl)
        .catch(parseHttpError)
      : { data: undefined }
    // Get robotAccount
    const robotAccountUrl = URLS.admin.robotAccount.replace('{robotId}', String(serialId))
    const { data: robotAccount } = serialId
      ? await thunkApi.extra.$http
        .get<RobotAccount>(robotAccountUrl)
        .catch(e => {
          if (e?.status === 404) {
            // no account
            return { data: undefined }
          } else {
            return parseHttpError(e)
          }
        })
      : { data: undefined }
    // Get chain
    const chainUrl = `${URLS.admin.chains}/${chainId}`

    const { data: chainResponse } = chainId
      ? await thunkApi.extra.$http
        .get<DetailedSubscriptionChainResponse>(chainUrl)
        .catch(parseHttpError)
      : { data: undefined }

    const taasUrl = `${URLS.admin.taas}/${taasId}`
    const { data: taasResponse } =
    taasId ? await thunkApi.extra.$http
      .get<DetailedTaasResponse>(taasUrl)
      .catch(parseHttpError)
      : { data: undefined }

    const robot: DetailedRobot = mapRobotDetailedResponseToRobotDetailed(robotResponse, robotAccount)
    const chain: DetailedSubscriptionChain = mapChainDetailedResponseToChainDetailed(chainResponse)
    const taas: DetailedTaas = mapTaasDetailedResponseToTaasDetailed(taasResponse)

    return {
      taas,
      robot,
      chain,
      relation: taas?.relation || robot?.relation || chain?.relation
    }
  }
)

export const searchSubscriptionsDashtransfer = createAsyncThunk<
SummarySubscription[],
{ relationId: string },
{ extra: { $http: IHttpService } }
>(
  'dashtransfer/searchSubscriptionsDashtransfer',
  async ({ relationId }, thunkApi) => {
    const url = `${URLS.admin.subscriptions}?relationId=${relationId}`

    const { data: subs } =
    await thunkApi.extra.$http
      .get<SummarySubscription[]>(url)
      .catch(parseHttpError)

    return subs
  }
)

export const searchRobotsDashtransfer = createAsyncThunk<
DetailedRobot[],
{ serial: string, relationId: string },
{ extra: { $http: IHttpService } }
>(
  'dashtransfer/searchRobotsDashtransfer',
  async ({ serial, relationId }, thunkApi) => {
    const url = `${URLS.admin.robots}?serial=${
      serial ? serial.replace('T-', '') : ''
    }${relationId ? `&relationId=${relationId}` : ''}`

    const { data: robotsResponse } =
    await thunkApi.extra.$http
      .get<DetailedRobotResponse[]>(url)
      .catch(parseHttpError)

    return robotsResponse.map((rob) => (mapRobotDetailedResponseToRobotDetailed(rob)))
  }
)

export const getNewRobot = createAsyncThunk<
DetailedRobot & { subscriptions: SummarySubscriptionItem[] },
DetailedRobot,
{ extra: { $http: IHttpService } }
>('dashtransfer/getNewRobot', async (robot, thunkApi) => {
  const chainUrl = `${URLS.admin.chains}/${robot?.chainId}`
  const robotUrl = `${URLS.admin.robots}/${robot?.id}`
  const { data } = await thunkApi.extra.$http.get<DetailedRobot>(robotUrl).catch(parseHttpError)

  const mappedData = mapRobotDetailedResponseToRobotDetailed(data)
  const payload: DetailedRobot & { subscriptions: SummarySubscriptionItem[] } = {
    ...mappedData,
    subscriptions: []
  }
  if (robot?.chainId) {
    const { data: chainResponse } =
    await thunkApi.extra.$http
      .get<DetailedSubscriptionChainResponse>(chainUrl)
      .catch(parseHttpError)
    payload.subscriptions = mapChainDetailedResponseToChainDetailed(chainResponse).subscriptions
  }
  return payload
})

export const searchRelationsDashtransfer = createAsyncThunk<
RelationDetailed[],
{ name: string },
{ extra: { $http: IHttpService } }
>('dashtransfer/searchRelationsDashtransfer', async ({ name }, thunkApi) => {
  const url = `${URLS.admin.relations}?name=${encodeURI(name)}`
  const { data: relations } = await thunkApi.extra.$http
    .get<OldRelationDetailed[]>(url)
    .catch(parseHttpError)
  return relations.map((r) => ({ id: r.relationId, relationType: r.relationType, name: r.name, teamleaderId: r.teamleaderId, teamleaderType: r.teamleaderType, type: r.type, isCustomer: r.isCustomer }))
})

export const searchTaasDashtransfer = createAsyncThunk<
DetailedTaas[],
{ relationId: string },
{ extra: { $http: IHttpService } }
>('dashtransfer/v', async ({ relationId }, thunkApi) => {
  const url = `${URLS.admin.taas}${
    relationId ? `?relationId=${relationId}` : ''
  }`
  const { data: taasResponse } = await thunkApi.extra.$http
    .get<DetailedTaasResponse[]>(url)
    .catch(parseHttpError)
  return taasResponse.map((t) => mapTaasDetailedResponseToTaasDetailed(t))
})
